import React, { useMemo, useEffect, useState } from "react";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Divider } from "primereact/divider";
import { ProgressSpinner } from "primereact/progressspinner";
import { Message } from "primereact/message";
import { BUSINESS_FIELDS } from "../constants";
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import ArticleCard from "../../../components/ArticleCard/ArticleCard";
import styles from "../style.module.css";

/**
 * BusinessCard Component
 * Displays detailed information about a business
 *
 * @param {Object} props - Component props
 * @param {Object} props.business - Business data to display
 * @param {Function} [props.onClose] - Callback when close button is clicked
 * @param {Function} [props.onViewProfile] - Callback when view profile button is clicked
 * @param {Function} [props.onGetDirections] - Callback when get directions button is clicked
 * @param {string} [props.travelMode] - Current travel mode for directions
 * @param {boolean} [props.isRequestingDirections] - Whether directions are being loaded
 * @param {string} [props.directionsError] - Error message for directions
 * @param {Array} [props.articles] - Array of articles for this business
 * @param {boolean} [props.loadingArticles] - Whether articles are being loaded
 * @param {string} [props.articlesError] - Error message for articles
 * @returns {JSX.Element} Business card component
 */
const BusinessCard = ({
  business,
  onClose,
  onViewProfile,
  onGetDirections,
  travelMode = null,
  isRequestingDirections = false,
  directionsError = "",
  articles = [],
  loadingArticles = false,
  articlesError = null,
}) => {
  // Format travel mode for display
  const travelModeLabel = useMemo(() => {
    if (!travelMode) return "Get Directions";
    const modeLabel =
      travelMode === "TWO_WHEELER"
        ? "Bike"
        : travelMode.charAt(0) + travelMode.slice(1).toLowerCase();
    return `${modeLabel}`;
  }, [travelMode]);

  // Format business hours
  const formattedHours = useMemo(() => {
    if (!business?.[BUSINESS_FIELDS.BUSINESS_HOURS]) return null;
    try {
      const hours =
        typeof business[BUSINESS_FIELDS.BUSINESS_HOURS] === "string"
          ? JSON.parse(business[BUSINESS_FIELDS.BUSINESS_HOURS])
          : business[BUSINESS_FIELDS.BUSINESS_HOURS];

      const days = [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday",
      ];
      return days.map((day) => ({
        day,
        hours: hours[day.toLowerCase()] || "Closed",
      }));
    } catch (e) {
      console.error("Error parsing business hours:", e);
      return null;
    }
  }, [business?.[BUSINESS_FIELDS.BUSINESS_HOURS]]);

  const categoriesString = useMemo(() => {
    if (!business) return "";
    const categories = business[BUSINESS_FIELDS.CATEGORIES] || [];
    return categories.join(" • ");
  }, [business?.[BUSINESS_FIELDS.CATEGORIES]]);

  const fullAddress = useMemo(() => {
    if (!business) return "";
    const parts = [
      business[BUSINESS_FIELDS.ADDRESS],
      business[BUSINESS_FIELDS.CITY],
      business[BUSINESS_FIELDS.STATE],
      business[BUSINESS_FIELDS.ZIP],
    ].filter(Boolean);
    return parts.join(", ");
  }, [
    business?.[BUSINESS_FIELDS.ADDRESS],
    business?.[BUSINESS_FIELDS.CITY],
    business?.[BUSINESS_FIELDS.STATE],
    business?.[BUSINESS_FIELDS.ZIP],
  ]);

  // Now we can do the early return
  if (!business) return null;

  const shopName = business[BUSINESS_FIELDS.SHOP_NAME] || "Business Name";
  const rating = business[BUSINESS_FIELDS.RATING] || 0;
  const reviewCount = business[BUSINESS_FIELDS.REVIEW_COUNT] || 0;
  const imageUrl = business[BUSINESS_FIELDS.STORE_IMAGE];
  const description =
    business[BUSINESS_FIELDS.DESCRIPTION] || "No description available.";
  const phone = business[BUSINESS_FIELDS.PHONE];
  const email = business[BUSINESS_FIELDS.CONTACT_EMAIL];
  const website = business.website;

  // Format date helper
  const formatArticleDate = (timestamp) => {
    if (!timestamp?.seconds) return '';
    const date = new Date(timestamp.seconds * 1000);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // Render article list
  const renderArticles = () => {
    if (!business.allArticles?.length) return null;

    return (
      <div className="mt-4">
        <h4 className="font-semibold mb-3">Articles</h4>
        <div className="space-y-4">
          {business.allArticles.map((article, index) => (
            <div 
              key={article.id} 
              className={`p-3 rounded-lg border ${index < (business.matchingArticles?.length || 0) ? 'bg-blue-50 border-blue-100' : 'bg-white border-gray-100'}`}
            >
              <div className="flex gap-3">
                {article.thumbnail?.url && (
                  <div className="w-16 h-16 flex-shrink-0">
                    <img 
                      src={article.thumbnail.url}
                      alt={article.thumbnail.alt || article.title}
                      className="w-full h-full object-cover rounded"
                    />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  {index < (business.matchingArticles?.length || 0) && (
                    <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full mb-1">
                      Matches your search
                    </span>
                  )}
                  <h4 className="font-medium text-gray-900 line-clamp-2">{article.title}</h4>
                  {article.summary && (
                    <p className="text-sm text-gray-600 line-clamp-2 mt-1">
                      {article.summary}
                    </p>
                  )}
                  {(article.updatedAt || article.createdAt) && (
                    <div className="text-xs text-gray-500 mt-1">
                      {formatArticleDate(article.updatedAt || article.createdAt)}
                    </div>
                  )}
                  {article.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {article.tags.map((tag, i) => (
                        <span 
                          key={i} 
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render articles section with searched article highlighted
  const renderArticlesSection = () => {
    // If we have articles, use them directly
    if (articles.length === 0) return null;
    
    // Use the first article as the featured/matching article
    const [searchedArticle, ...otherArticles] = articles;
    
    // Safely access thumbnail URL
    const getThumbnailUrl = (article) => {
      if (!article) return null;
      if (article.thumbnail?.url) return article.thumbnail.url;
      if (article.imageUrl) return article.imageUrl;
      return null;
    };
    
    const getThumbnailAlt = (article) => {
      if (!article) return '';
      if (article.thumbnail?.alt) return article.thumbnail.alt;
      if (article.title) return article.title;
      return '';
    };
    
    return (
      <div className={styles.articlesSection}>
        <h4>Articles</h4>
        {loadingArticles ? (
          <div className="flex justify-content-center p-4">
            <ProgressSpinner style={{ width: "30px", height: "30px" }} />
          </div>
        ) : articlesError ? (
          <Message
            severity="error"
            text={articlesError}
            className="p-message-sm"
          />
        ) : articles.length > 0 ? (
          <div className="space-y-4">
            {/* Show searched article first if it exists */}

            {searchedArticle && (
              <div className="bg-blue-50 mb-2 rounded-lg border border-blue-100">
                <div className="text-sm font-medium text-blue-800 mb-2">Matching Article</div>
                <ArticleCard 
                  article={{
                    ...searchedArticle,
                    thumbnail: getThumbnailUrl(searchedArticle) ? {
                      url: getThumbnailUrl(searchedArticle),
                      alt: getThumbnailAlt(searchedArticle)
                    } : null,
                    summary: searchedArticle.introduction || searchedArticle.summary || searchedArticle.excerpt
                  }}
                  className="mb-2"
                />
              </div>
            )}
            
            {/* Show other articles */}
            {otherArticles.length > 0 && (
              <>
                <div className="text-sm font-medium text-gray-700 mt-4 mb-2">
                  {searchedArticle ? 'Other Articles' : 'Latest Articles'}
                </div>
                <div className={styles.articlesGrid}>
                  {otherArticles.slice(0, 3).map((article) => (
                    <ArticleCard 
                      key={article.id} 
                      article={{
                        ...article,
                        thumbnail: getThumbnailUrl(article) ? {
                          url: getThumbnailUrl(article),
                          alt: getThumbnailAlt(article)
                        } : null,
                        summary: article.introduction || article.summary || article.excerpt
                      }}
                      className="h-full"
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className={styles.noArticles}>
            No articles found for this business.
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.businessCard}>
      <div className="flex justify-content-between align-items-center mb-3">
        <h2 className="text-2xl font-bold m-0">{shopName}</h2>
        {onClose && (
          <Button
            icon="pi pi-times"
            className="p-button-text p-button-rounded p-button-plain"
            onClick={onClose}
            aria-label="Close"
          />
        )}
      </div>

      {imageUrl && (
        <div className={styles.businessImageContainer}>
          <img
            src={imageUrl}
            alt={shopName}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "path/to/default-image.jpg";
            }}
          />
        </div>
      )}

      {categoriesString && (
        <div className="text-sm text-gray-500 mb-2">
          {business.searchMatch && (
            <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mb-2">
              {business.searchMatch}
            </div>
          )}
          {categoriesString}
        </div>
      )}

      <p className="mt-0 mb-4">
        {description.length > 200
          ? `${description.substring(0, 200)}...`
          : description}
      </p>

      <Divider />

      <div className="mb-4">
        <h4 className="text-lg font-medium mb-2">Contact Information</h4>

        {fullAddress && (
          <div className="flex align-items-start mb-2">
            <i className="pi pi-map-marker mr-2 mt-1 text-500"></i>
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                fullAddress
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.noUnderline}
            >
              {fullAddress}
            </a>
          </div>
        )}

        {phone && (
          <div className="flex align-items-center mb-2">
            <i className="pi pi-phone mr-2 text-500"></i>
            <a href={`tel:${phone}`} className={styles.noUnderline}>
              {phone}
            </a>
          </div>
        )}

        {email && (
          <div className="flex align-items-center mb-2">
            <i className="pi pi-envelope mr-2 text-500"></i>
            <a href={`mailto:${email}`} className={styles.noUnderline}>
              {email}
            </a>
          </div>
        )}

        {website && (
          <div className="flex align-items-center">
            <i className="pi pi-globe mr-2 text-500"></i>
            <a
              href={website.startsWith("http") ? website : `https://${website}`}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.noUnderline}
            >
              {website.replace(/^https?:\/\//, "")}
            </a>
          </div>
        )}
      </div>

      {formattedHours && (
        <div>
          {renderArticles()}
          <div className="mt-6">
            <h4 className="font-semibold mb-2">Business Hours</h4>
            <div className="grid">
              {formattedHours.map(({ day, hours }) => (
                <React.Fragment key={day}>
                  <div className="col-4">{day}</div>
                  <div className="col-8 font-medium">{hours}</div>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mt-4">
        {onViewProfile && (
          <Button
            label="View Profile"
            icon="pi pi-user"
            className="p-button-text"
            onClick={onViewProfile}
          />
        )}
        {onGetDirections && (
          <Button
            label={travelModeLabel}
            icon="pi pi-directions"
            className="p-button-outlined"
            onClick={onGetDirections}
            loading={isRequestingDirections}
          />
        )}
      </div>

      {renderArticlesSection()}

      {directionsError && (
        <Message
          severity="error"
          text={directionsError}
          className="mt-3 p-message-sm"
        />
      )}
    </div>
  );
};

export default BusinessCard;
